import Modal from "@/components/base/Modal";
import { formatMoney } from "@/lib/format";
import { buildWhatsappUrl } from "@/pages/catalog/catalogUtils";
import type { PublicCatalogItem } from "@/types";

interface CatalogProductModalProps {
  item: PublicCatalogItem | null;
  showPrices: boolean;
  showWhatsapp: boolean;
  whatsappNumber: string | null;
  onClose: () => void;
}

const FALLBACK_IMAGE =
  "https://readdy.ai/api/search-image?query=Clean%20studio%20product%20photograph%20of%20a%20heavy%20duty%20black%20truck%20tire%20standing%20upright%20on%20a%20seamless%20warm%20beige%20neutral%20background%2C%20soft%20even%20studio%20lighting%2C%20gentle%20natural%20shadow%2C%20minimal%20commercial%20catalog%20aesthetic%2C%20high%20detail&width=600&height=450&seq=public-catalog-tire-placeholder&orientation=landscape";

export default function CatalogProductModal({
  item,
  showPrices,
  showWhatsapp,
  whatsappNumber,
  onClose,
}: CatalogProductModalProps) {
  if (!item) return null;

  const whatsappUrl = buildWhatsappUrl(
    whatsappNumber,
    `Hola, me interesa el producto: ${item.name}${item.sku ? ` (${item.sku})` : ""}.`,
  );

  const specs: Array<{ label: string; value: string | null }> = [
    { label: "Código", value: item.sku },
    { label: "Marca", value: item.brand_name },
    { label: "Medida", value: item.tire_size },
    { label: "Modelo", value: item.model },
    { label: "Categoría", value: item.category_name },
    { label: "Unidad", value: item.unit },
  ].filter((row) => Boolean(row.value));

  return (
    <Modal open={Boolean(item)} onClose={onClose} size="md" title={item.name} subtitle="Detalle del producto">
      <div className="flex flex-col gap-5">
        <div className="h-56 w-full overflow-hidden rounded-lg border border-background-200 bg-background-100">
          <img
            src={item.image_url || FALLBACK_IMAGE}
            alt={item.name}
            title={`${item.name} ${item.tire_size ?? ""} neumático`.trim()}
            className="h-full w-full object-cover"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {item.category_name ? (
            <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
              {item.category_name}
            </span>
          ) : null}
          {item.brand_name ? (
            <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-semibold text-secondary-900">
              {item.brand_name}
            </span>
          ) : null}
          {item.tire_size ? (
            <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-semibold text-accent-900">
              {item.tire_size}
            </span>
          ) : null}
        </div>

        {showPrices ? (
          <div className="rounded-lg border border-background-200 bg-background-100 px-4 py-3">
            <p className="text-xs text-foreground-500">Precio</p>
            <p className="font-heading text-2xl font-bold text-foreground-950">{formatMoney(item.price)}</p>
          </div>
        ) : null}

        {item.catalog_description ? (
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground-900">Descripción</h3>
            <p className="mt-1.5 whitespace-pre-line text-sm leading-relaxed text-foreground-600">
              {item.catalog_description}
            </p>
          </div>
        ) : null}

        {specs.length > 0 ? (
          <div>
            <h3 className="font-heading text-sm font-semibold text-foreground-900">Especificaciones</h3>
            <dl className="mt-2 grid grid-cols-1 gap-x-4 gap-y-2 sm:grid-cols-2">
              {specs.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between gap-3 rounded-md border border-background-200 bg-background-50 px-3 py-2"
                >
                  <dt className="text-xs text-foreground-500">{row.label}</dt>
                  <dd className="truncate text-xs font-semibold text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}

        {showWhatsapp && whatsappUrl ? (
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-whatsapp-line text-base"></i>
            Consultar por WhatsApp
          </a>
        ) : null}
      </div>
    </Modal>
  );
}